import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useMailboxes } from '../hooks/useMailboxes';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectedProps {
  accountId: number | null;
  mailboxUuid: string;
  address: string;
  /** The campaign of the setup: the test is its sample pitch. Null: a fixed sample. */
  campaignUuid: string | null;
}

/** The mailbox is connected (design outreach-v2 o7): the warm-up line and "Send me a test". */
const ConnectedComponent: React.FC<ConnectedProps> = ({ accountId, mailboxUuid, address, campaignUuid }) => {
  const intl = useIntl();
  const { sendTest, isSendingTest } = useMailboxes(accountId);
  const [result, setResult] = useState<string | null>(null);

  const test = () => {
    setResult(null);
    sendTest({ mailboxUuid, campaignUuid })
      .then((answer) =>
        setResult(
          answer.ok ? intl.formatMessage(messages.testSent, { to: answer.to }) : intl.formatMessage(messages.testFailed)
        )
      )
      .catch(() => setResult(intl.formatMessage(messages.testFailed)));
  };

  return (
    <div className={styles.body} data-testid="mailbox-connected">
      <div className={styles.connected}>
        <span className={styles.connectedMark}>
          <FontAwesomeIcon icon={faCheck} />
        </span>
        <h2 className={styles.title}>{intl.formatMessage(messages.connectedTitle, { address })}</h2>
        <p className={styles.intro}>{intl.formatMessage(messages.connectedBody)}</p>
      </div>
      <div className={styles.help}>
        <b>{intl.formatMessage(messages.testTitle)}</b>
        {intl.formatMessage(messages.testBody)}
        <div className={styles.actions}>
          <SMLButton size="sm" outline omitCap leftIcon={faPaperPlane} isLoading={isSendingTest} onClick={test}>
            {intl.formatMessage(messages.testButton)}
          </SMLButton>
        </div>
        {result !== null && <span role="status">{result}</span>}
      </div>
    </div>
  );
};

export const Connected = TypedMemo(ConnectedComponent);
